// src/components/fieldset.jsx
'use client';

import React, { createContext, useContext, useId } from 'react';
import clsx from 'clsx';

const FieldCtx = createContext({
  id: undefined,
  describedBy: undefined,
  errorId: undefined,
  hasError: false,
});

export function Field({ children, className, error, id, ...props }) {
  const fallbackId = useId();
  const fieldId = id || `field-${fallbackId}`;
  const descId = `${fieldId}-desc`;
  const errId = `${fieldId}-err`;
  const hasError = Boolean(error);

  return (
    <FieldCtx.Provider
      value={{
        id: fieldId,
        describedBy: hasError ? `${descId} ${errId}` : descId,
        errorId: errId,
        hasError,
      }}
    >
      <div className={clsx('space-y-1.5', className)} {...props}>
        {children}
      </div>
    </FieldCtx.Provider>
  );
}

export function Label({ children, htmlFor, className, ...props }) {
  const ctx = useContext(FieldCtx);
  return (
    <label
      htmlFor={htmlFor || ctx.id}
      className={clsx('block text-sm font-medium text-zinc-800 dark:text-zinc-200', className)}
      {...props}
    >
      {children}
    </label>
  );
}

// Some files import `Label as CatalystLabel`; keep named export alias compatible if needed
export { Label as CatalystLabel };

export function Description({ children, className, ...props }) {
  const ctx = useContext(FieldCtx);
  if (!children) return null;
  return (
    <p
      id={`${ctx.id}-desc`}
      className={clsx('text-xs text-zinc-500 dark:text-zinc-400', className)}
      {...props}
    >
      {children}
    </p>
  );
}

export function ErrorMessage({ children, className, ...props }) {
  const ctx = useContext(FieldCtx);
  if (!children) return null;
  return (
    <p
      id={ctx.errorId}
      role="alert"
      className={clsx('text-xs text-red-600 dark:text-red-400', className)}
      {...props}
    >
      {children}
    </p>
  );
}
